import type { InjectionKey } from 'vue'
import { inject } from 'vue'
import type { Dependency } from 'mflows'
import type { useWorkflowStore } from 'mflows'
import type { Variable } from 'mflows'
import type { ResourceItem, ResourceType } from 'mflows'
// 新增：引入具名类型，提升对外 API 的类型体验
import type { ThemeManager } from '@/components/theme/themeManager'
import type { NodeManager } from '@/components/flow/services/nodeManager'

export interface MFlowAPI {
  // Workflow store API directly exposed for convenience
  workflow: ReturnType<typeof useWorkflowStore>

  // Dependency management APIs for custom nodes/functions
  dependencies: {
    state: any
    addDependency: (dep: Omit<Dependency, 'id'>) => void
    deleteDependency: (id: string) => void
    getDependencies: (type?: 'umd' | 'cjs' | 'es') => Dependency[]
    loadDependency: (id: string) => Promise<void>
    getLoadedDependency: (id: string) => any
    createFunctionContext: () => Record<string, any>
  }

  // Variables management
  variables: {
    getGlobalVariables: () => Variable[]
    getWorkflowVariables: () => Variable[]
    addGlobalVariable: (v: Variable) => void
    addWorkflowVariable: (v: Variable) => void
    updateGlobalVariable: (v: Variable) => void
    updateWorkflowVariable: (v: Variable) => void
    deleteGlobalVariable: (key: string) => void
    deleteWorkflowVariable: (key: string) => void
  }

  // Resource management
  resources: {
    getResources: (filter?: ResourceType | 'all') => ResourceItem[]
    addResourceFromFile: (file: File) => ResourceItem
    deleteResource: (id: string) => void
    createJsonResource: (name?: string) => ResourceItem
  }

  // Basic UI controls so extensions can toggle panels if needed
  ui: {
    getLeftVisible: () => boolean
    setLeftVisible: (v: boolean) => void
    toggleLeft: () => void
    getRightVisible: () => boolean
    setRightVisible: (v: boolean) => void
    toggleRight: () => void
    getBottomVisible: () => boolean
    setBottomVisible: (v: boolean) => void
    toggleBottom: () => void
  }

  // Managers for advanced integrations
  nodeManager: NodeManager
  themeManager: ThemeManager
}

export const MFlowInjectionKey: InjectionKey<MFlowAPI> = Symbol('MFlowAPI')

export function useMFlow(): MFlowAPI {
  const api = inject(MFlowInjectionKey)
  if (!api) {
    throw new Error('[mflow] useMFlow() called without provider. Make sure to render within <MFlowEditor>.')
  }
  return api
}